import { BuilderComponent, BuilderContent } from '@builder.io/react'
import { GrowthBookProvider } from '@growthbook/growthbook-react'
import { Auth } from 'aws-amplify'
import { getCognitoId, getVisitorId } from 'components/util/UserId'
import { localPhoneNumberReadable } from 'constants/phone'
import { graphql } from 'gatsby'
import React, { createContext, useContext, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import PageLayout from '../../components/ui-builder/layouts/PageLayout'
import CookieBanner from '../../components/ui/cookie-banner/CookieBanner'
import { gb, loadGrowthbook } from '../../components/util/Growthbook'

const defaultTitle = 'Different Dog'
const defaultDescription =
  'Natural, personalised hand-cooked meals made with quality ingredients for dogs & pups. Delivered across the UK. Start your healthy dog food subscription today!'

interface BuilderContextProps {
  isLoggedIn: boolean
  setIsLoggedIn: (newValue: boolean) => void
}
export const BuilderContext = React.createContext<BuilderContextProps>({} as BuilderContextProps)

// function PageProvider({ children }: { children: React.ReactNode }) {
//   const [isLoggedIn, setIsLoggedIn] = useState(false)

//   return <BuilderContext.Provider value={{ isLoggedIn, setIsLoggedIn }}>{children}</BuilderContext.Provider>
// }

function PageTemplate({ data }: any) {
  const PageContents = () => {
    const { isLoggedIn, setIsLoggedIn } = useContext(BuilderContext)!

    const [templateContent, setTemplateContent] = useState(data?.allBuilderModels?.page[0]?.content)
    // const templateContent = data?.allBuilderModels?.page[0]?.content
    const [page, setPage] = useState(data?.allBuilderModels.page[0])
    const [isSignedIn, setIsSignedIn] = useState(false)
    const [visitorId, setVisitorId] = useState('')
    const [cognitoId, setCognitoId] = useState('')
    const user_id = cognitoId || visitorId

    useEffect(() => {
      const callIdsForGtag = async () => {
        setVisitorId(await getVisitorId())
        setCognitoId(await getCognitoId())
      }
      callIdsForGtag()
    }, [])

    useEffect(() => {
      const checkIfSignedIn = async () => {
        const username = await getCognitoId()
        if (username) {
          setIsSignedIn(true)
        } else {
          setIsSignedIn(false)
        }
      }
      checkIfSignedIn()
    }, [])

    // let enabled = false
    // Homepage AB test
    // if (templateContent?.data.url === '/') {
    //   enabled = useFeatureIsOn('home-page')
    // }

    // let page = data?.allBuilderModels.page[0]
    const pageData = page?.content?.data
    useEffect(() => {
      if (data?.allBuilderModels?.page?.length > 1) {
        if (isSignedIn) {
          setTemplateContent(data?.allBuilderModels?.page[cognitoId ? 1 : 0].content)
          setPage(data?.allBuilderModels.page[cognitoId ? 1 : 0])
        } else {
          setTemplateContent(data?.allBuilderModels?.page[0]?.content)
          setPage(data?.allBuilderModels?.page[0])
        }
      } else {
        setTemplateContent(data?.allBuilderModels?.page[0]?.content)
        setPage(data?.allBuilderModels?.page[0])
      }
    }, [cognitoId, isSignedIn])

    useEffect(() => {
      const loadGb = async () => {
        if (user_id) {
          await loadGrowthbook(gb)
        }
      }
      loadGb()
    }, [user_id])

    const localBusinessSchema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Different Dog',
      description: defaultDescription,
      image:
        'https://cdn.builder.io/api/v1/image/assets%2F0e0b081978d0442ca5f36e71c7712df5%2F5785df7e72df4e509507f52d3ae4824d',
      logo: 'https://cdn.builder.io/api/v1/image/assets%2F0e0b081978d0442ca5f36e71c7712df5%2Fb88757344e1748b9bbc5bde2f8ca27c8',
      '@id': '',
      url: 'https://www.differentdog.com/',
      telephone: localPhoneNumberReadable,
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Unit 3, Pioneer Park',
        addressLocality: 'Telford',
        postalCode: 'TF7 4EF',
        addressCountry: 'GB'
      },
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: localPhoneNumberReadable,
        contactType: 'customer service',
        areaServed: 'GB',
        availableLanguage: ['English']
      },
      sameAs: [
        'https://www.facebook.com/differentdog/?locale=en_GB',
        'https://twitter.com/_differentdog?lang=en',
        'https://www.instagram.com/differentdog/?hl=en'
      ],
      foundingDate: '2017',
      areaServed: 'GB'
    }

    const ogImage =
      pageData?.ogImage ||
      templateContent?.data?.ogImage ||
      page?.ogImage ||
      'https://cdn.builder.io/api/v1/image/assets%2F0e0b081978d0442ca5f36e71c7712df5%2F3f2a8a2615424441b49778e088b807ad'

    return (
      <BuilderContent model="page" content={page}>
        {page => {
          const noIndex = page?.noIndex || process.env.GATSBY_USER_BRANCH !== 'production'
          // console.log('PageTemplate:: Template is changing', page)
          return (
            <PageLayout
              headerIdentifier={pageData?.header?.id || page?.header?.id || ''}
              footerIdentifier={page?.footer?.id || ''}
              bannerEnable={page?.enableTopBanner}
              bannerFixed={pageData?.enableFixedBanner}
              activeBannerIdentifier={page?.activeBanner?.id || ''}
              activeLoggedInBannerIdentifier={page?.activeLoggedInBanner?.id || ''}
              title={page?.title || defaultTitle}
              headerWrapperBackgroundColor={page?.headerWrapperBackgroundColor}
            >
              <Helmet>
                {/* meta tags for page */}
                <title>{page?.title || defaultTitle}</title>
                <meta property="og:description" content={pageData?.ogDescription || page?.description || ''} />
                <meta name="description" content={page?.description || pageData?.ogDescription || ''} />
                <meta property="og:site_name" content={pageData?.siteName || 'Different Dog'} />
                <meta property="og:title" content={pageData?.ogTitle || page?.title || defaultTitle} />
                <meta property="og:type" content={pageData?.ogType || 'website'} />
                <meta
                  property="og:url"
                  content={
                    (pageData?.ogUrl || page?.canonical || templateContent?.data?.url)?.substr(0, 1) === '/'
                      ? (pageData?.ogUrl || page?.canonical || templateContent?.data?.url).replace(
                          '/',
                          'https://www.differentdog.com/'
                        )
                      : pageData?.ogUrl || page?.canonical || templateContent?.data.url
                  }
                />
                <meta property="og:image" content={ogImage} />
                {noIndex && <meta name="robots" content="noindex" />}
                <link
                  rel="canonical"
                  href={
                    (page?.canonical || templateContent?.data?.url)?.substr(0, 1) === '/'
                      ? (page?.canonical || templateContent?.data?.url).replace('/', 'https://www.differentdog.com/')
                      : page?.canonical || templateContent?.data.url
                  }
                />
                {/* meta tags ends for page */}
                {/* Local business schema */}
                <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
                {/* <!-- Gtag script --> */}
                <script>{`
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
              `}</script>
                {/* <!-- End Gtag script --> */}
                {/* Plausible Analytics script */}
                {/* <script defer data-api="/api/event" data-domain="differentdog.com" src="/js/script.js"></script> */}
              </Helmet>
              {/* <h2>Page template</h2> */}
              {/** name of the model is landing page, change it if you decided to build*/}
              <BuilderComponent model="page" content={templateContent} />
              <CookieBanner />
            </PageLayout>
          )
        }}
      </BuilderContent>
    )
  }

  return (
    <GrowthBookProvider growthbook={gb}>
      <PageContents />
    </GrowthBookProvider>
  )
}

export default PageTemplate

// export const PageTemplateQuery = graphql`
//   query ($path: String!) {
//     allBuilderModels {
//       page(target: { urlPath: $path }, options: { cachebust: true }) {
//         content
//         data {
//           description
//           title
//           footer
//           header
//           activeBanner
//           enableTopBanner
//         }
//         localFiles {
//           publicURL
//         }
//       }
//     }
//   }
// `

export const PageTemplateQuery = graphql`
  query ($path: String!) {
    allBuilderModels {
      page(target: { urlPath: $path }, options: { cachebust: true }) {
        content
        data {
          description
          title
          footer
          header
          activeBanner
          activeLoggedInBanner
          enableTopBanner
          noIndex
          headerWrapperBackgroundColor
        }
      }
    }
  }
`
